import { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { AireSubscriptionContext, PraticaKey, SubscriptionForeignResidenceStep } from './index';

interface AireSubscriptionProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'aire_subscription_state';

interface PersistedState {
  currentStep: SubscriptionForeignResidenceStep;
  intestatarioData: unknown | null;
  praticaKey: PraticaKey | null;
}

const loadState = (): PersistedState | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveState = (state: PersistedState): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    return;
  }
};

const AireSubscriptionProvider = ({ children }: AireSubscriptionProviderProps): ReactElement => {
  const persistedState = loadState();

  const [currentStep, setCurrentStepState] = useState<SubscriptionForeignResidenceStep>(
    persistedState?.currentStep ?? SubscriptionForeignResidenceStep.RegistryOffice
  );
  const [intestatarioData, setIntestatarioDataState] = useState<unknown | null>(
    persistedState?.intestatarioData ?? null
  );
  const [praticaKey, setPraticaKeyState] = useState<PraticaKey | null>(
    persistedState?.praticaKey ?? null
  );

  useEffect(() => {
    saveState({
      currentStep,
      intestatarioData,
      praticaKey,
    });
  }, [currentStep, intestatarioData, praticaKey]);

  const setCurrentStep = useCallback((step: SubscriptionForeignResidenceStep) => {
    setCurrentStepState(step);
  }, []);

  const setIntestatarioData = useCallback((data: unknown | null) => {
    setIntestatarioDataState(data);
  }, []);

  const setPraticaKey = useCallback((key: PraticaKey | null) => {
    setPraticaKeyState(key);
  }, []);

  const clearState = useCallback(() => {
    setCurrentStepState(SubscriptionForeignResidenceStep.RegistryOffice);
    setIntestatarioDataState(null);
    setPraticaKeyState(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      return;
    }
  }, []);

  return (
    <AireSubscriptionContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        intestatarioData,
        setIntestatarioData,
        praticaKey,
        setPraticaKey,
        clearState,
      }}
    >
      {children}
    </AireSubscriptionContext.Provider>
  );
};

AireSubscriptionProvider.displayName = 'Aire Subscription Provider';

export default AireSubscriptionProvider;
